import { Dispatch, SetStateAction } from "react";
import { TeamMember, VisitorInformation } from "../../../features/Booking-Form/typings/team-member";
import {
  BookingType,
  NotSerializedBookingInputs,
  NotSerializedBookingService
} from "../../../features/Booking-Form/typings/booking-inputs";
import { setInputs } from "../../../features/Booking-Form/slices/booking.slice";
import { AppDispatch } from "../../../app/store";
import { getAllUsers, newConferenceZoneBookingActivityBased } from "../../../utils/axios-requests";
import { UserOrEmailMapping } from "../../../features/Admin/typings/admin.types";
import { BookingServicesDto } from "../../../features/Booking-Form/typings/booking.types";
import { ServiceType } from "../../../features/Booking-Form/typings/booking-service-administration.types";
import { BookingRequestsStatus } from "../../BookingRequest/typings/booking-requests.types";

export const isBookingUser = (user: TeamMember, sub: string) =>
  user.userId === sub && user.isExternal !== true && user.isVisitor !== true;

export const timezoneParsed = (
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string
) => (inputs.isMobileWorking ? timezoneMobileWorking : inputs.timezone);

export const bookingObjectParsed = (
  user: TeamMember,
  inputs: NotSerializedBookingInputs,
  sub: string
) => {
  if (inputs.isMobileWorking) return 1;
  else if (inputs.automatedSeating)
    return (
      inputs.automatedSeatingObjects?.find(object => object.userId === sub)?.bookingInventoryId || 0
    );
  else return user?.bookingInventoryId;
};

export const firstNameParsed = (user: TeamMember, withName?: boolean) =>
  withName ? user.firstName : undefined;
export const surnameParsed = (user: TeamMember, withName?: boolean) =>
  withName ? user.surname : undefined;
export const bookingObjectExternal = (user: TeamMember, inputs: NotSerializedBookingInputs) =>
  inputs.isMobileWorking ? 1 : user.bookingInventoryId;

export const externalParsed = (
  user: TeamMember,
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string,
  withName?: boolean
) => {
  const vi = user.visitorInformation;
  const visitReasonTrimmed = vi?.visitReason?.trim();
  const contactsTrimmed = vi?.contacts?.trim();

  return {
    userId: sub,
    isUserBookedBy: false,
    userBookedById: sub,
    externalInformation: {
      firstName: user.firstName,
      surname: user.surname,
      company: user.company,
      email: user.email,
      ...(contactsTrimmed ? { contacts: contactsTrimmed } : {}),
      ...(visitReasonTrimmed ? { visitReason: visitReasonTrimmed } : {})
    },
    isExternal: true,
    timezone: timezoneParsed(inputs, timezoneMobileWorking),
    bookingInventoryId: bookingObjectExternal(user, inputs),
    firstName: firstNameParsed(user, withName),
    surname: surnameParsed(user, withName),
    isMobileWorking: inputs.isMobileWorking
  };
};

export const bookingUserParsed = (
  user: TeamMember,
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string,
  withName?: boolean
) => ({
  userId: user.userId,
  firstName: firstNameParsed(user, withName),
  surname: surnameParsed(user, withName),
  isUserBookedBy: true,
  isExternal: false,
  userBookedById: sub,
  timezone: timezoneParsed(inputs, timezoneMobileWorking),
  bookingInventoryId: bookingObjectParsed(user, inputs, sub),
  isMobileWorking: inputs.isMobileWorking,
  email: user.email ?? ""
});

/** Visitor booking: booker row — uses isVisitor instead of legacy isExternal */
const bookingVisitorBookedUserParsed = (
  user: TeamMember,
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string,
  withName?: boolean
) => ({
  userId: user.userId,
  firstName: firstNameParsed(user, withName),
  surname: surnameParsed(user, withName),
  isUserBookedBy: true,
  isVisitor: false,
  userBookedById: sub,
  timezone: timezoneParsed(inputs, timezoneMobileWorking),
  bookingInventoryId: bookingObjectParsed(user, inputs, sub),
  isMobileWorking: inputs.isMobileWorking,
  email: user.email ?? ""
});

export const visitorInformationFromUser = (user: TeamMember): VisitorInformation => {
  if (user.visitorInformation) {
    return user.visitorInformation;
  }
  return {
    firstName: user.firstName ?? "",
    surname: user.surname ?? "",
    company: typeof user.company === "string" ? user.company : undefined,
    email: user.email ?? "",
    contacts: undefined,
    visitReason: undefined
  };
};

/** Visitor booking: guest row — visitorInformation + isVisitor (not externalInformation / isExternal) */
const visitorInformationParsed = (
  user: TeamMember,
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string,
  withName?: boolean
) => ({
  userId: sub,
  isUserBookedBy: false,
  userBookedById: sub,
  visitorInformation: visitorInformationFromUser(user),
  isVisitor: true,
  ...(user.bookPlace !== undefined ? { bookPlace: user.bookPlace } : {}),
  timezone: timezoneParsed(inputs, timezoneMobileWorking),
  bookingInventoryId: bookingObjectExternal(user, inputs),
  firstName: firstNameParsed(user, withName),
  surname: surnameParsed(user, withName),
  isMobileWorking: inputs.isMobileWorking
});

export const teamMemberParsed = (
  user: TeamMember,
  inputs: NotSerializedBookingInputs,
  sub: string,
  withName?: boolean
) => ({
  userId: user.userId,
  isUserBookedBy: false,
  userBookedById: sub,
  isExternal: false,
  firstName: firstNameParsed(user, withName),
  surname: surnameParsed(user, withName),
  bookingInventoryId: bookingObjectParsed(user, inputs, sub),
  isMobileWorking: inputs.isMobileWorking,
  email: user.email ?? ""
});

export const transformBookingUsers = (
  usersTransform: TeamMember[],
  sub: string,
  inputs: NotSerializedBookingInputs,
  timezoneMobileWorking: string,
  withName?: boolean
) =>
  usersTransform?.map((user: TeamMember) => {
    if (inputs.mode === BookingType.VISITOR) {
      if (isBookingUser(user, sub)) {
        return bookingVisitorBookedUserParsed(user, sub, inputs, timezoneMobileWorking, withName);
      }
      if (user.isVisitor === true) {
        return visitorInformationParsed(user, sub, inputs, timezoneMobileWorking, withName);
      }
      return teamMemberParsed(user, inputs, sub, withName);
    }
    if (isBookingUser(user, sub))
      return bookingUserParsed(user, sub, inputs, timezoneMobileWorking, withName);
    else if (user.isExternal === true)
      return externalParsed(user, sub, inputs, timezoneMobileWorking, withName);
    else return teamMemberParsed(user, inputs, sub, withName);
  });

/** Helper function for getUsersIfEmpty */
export function excludeCurrentUser(users: any[], sub: string) {
  return users.filter(user => user.userId !== sub);
}

/** Helper function for getUsersIfEmpty */
export function filterBookedUsers(users: any[], usersBookedFor?: TeamMember[]) {
  if (!usersBookedFor) return [];
  const bookedIds = new Set(usersBookedFor.map(u => u.userId));
  return users.filter(user => bookedIds.has(user.userId));
}

/** Helper function for getUsersIfEmpty */
export function updateConferenceInputs(setConferenceInputs: any, bookedUsers: any[]) {
  setConferenceInputs((prevState: any) => ({
    ...prevState,
    visitors: bookedUsers
  }));
}

/** Helper function for getUsersIfEmpty */
export function updateZoneAccessIfTeamMode(
  dispatch: AppDispatch,
  inputs: NotSerializedBookingInputs,
  bookedUsers: any[]
) {
  if (inputs.mode !== "team") return;
  dispatch(setInputs({ zoneAccess: bookedUsers }));
}

export const getUsersIfEmpty = async (
  users: UserOrEmailMapping[],
  sub: string,
  dispatch: AppDispatch,
  setUsers: Dispatch<SetStateAction<UserOrEmailMapping[]>>,
  setConferenceInputs: any,
  inputs: NotSerializedBookingInputs,
  usersBookedFor?: TeamMember[]
) => {
  if (users.length !== 0) return;

  const response = await getAllUsers();
  const allUsers = response.data;

  const filteredUsers = excludeCurrentUser(allUsers, sub);
  const bookedUsers = filterBookedUsers(filteredUsers, usersBookedFor);

  setUsers(filteredUsers);
  updateConferenceInputs(setConferenceInputs, bookedUsers);
  updateZoneAccessIfTeamMode(dispatch, inputs, bookedUsers);
};

export const conferenceBookingUsers = (
  conferenceInputs: any,
  inputs: NotSerializedBookingInputs,
  conferenceBooking: boolean,
  sub: string,
  timezoneMobileWorking: string,
  errorToState: (error: string) => void
) => {
  if (conferenceBooking) {
    newConferenceZoneBookingActivityBased({
      users: transformBookingUsers(
        inputs.usersBookedFor as TeamMember[],
        sub,
        inputs,
        timezoneMobileWorking,
        true
      ).filter((user: { isUserBookedBy: boolean }) => user.isUserBookedBy),
      startDate: conferenceInputs.startDate,
      endDate: conferenceInputs.endDate,
      startTime: conferenceInputs.startTime,
      endTime: conferenceInputs.endTime,
      weekdays: conferenceInputs.weekdays,
      bookingType: BookingType.CONFERENCEZONE,
      locationInventoryId: inputs.selectedLocation,
      subject: inputs.subject,
      description: inputs.description,
      bookingServicesDto: handleToBookingServiceDto(
        inputs.usersBookedFor?.[0]?.bookingInventoryId,
        inputs.conferenceService
      ),
      conferenceZoneBookingGuests: conferenceInputs.guests ?? inputs.zoneAccess ?? [],
      specificDays: inputs.specificDays,
      frequence: inputs.frequence,
      interval: inputs.interval,
      bookingMonthDay: inputs.bookingMonthDay,
      bookingYearDay: inputs.bookingYearDay,
      onlineMeetingProvider: inputs.onlineMeetingProvider || null
    })
      .then()
      .catch(err => errorToState(err.response.data.message));
  }
};

export const weekdayInput = (inputs: NotSerializedBookingInputs) =>
  Object.values(inputs.weekdays).some(Boolean) ? inputs.weekdays : undefined;

export const handleToBookingServiceDto = (
  bookingInventoryId?: number,
  conferenceService?: NotSerializedBookingService | undefined
): BookingServicesDto[] => {
  if (!conferenceService || !bookingInventoryId) return [];

  const selectedService = conferenceService[bookingInventoryId];
  if (!selectedService) return [];

  const { caterings, bookableEquipments, roomLayout } = selectedService;

  let dtos: BookingServicesDto[] = [];

  // handle catering
  if (caterings.some(ca => ca.qty > 0)) {
    const cateringItems = caterings
      .map(cat => {
        if (cat.qty > 0) {
          return {
            serviceType: ServiceType.Catering,
            status: BookingRequestsStatus.REQUESTED,
            serviceId: cat.id,
            setUpTime: cat.setUpTime ?? 15,
            qty: cat.qty
          };
        }
      })
      .filter(item => item !== undefined);

    dtos = [...dtos, ...(cateringItems as BookingServicesDto[])];
  }

  // handle bookable equipments
  if (bookableEquipments.length > 0) {
    const equipments = bookableEquipments.map(eq => ({
      serviceType: ServiceType.Bookable_Equipment,
      status: BookingRequestsStatus.REQUESTED,
      serviceId: eq.id,
      setUpTime: eq.setUpTime ?? 15
    }));

    dtos = [...dtos, ...(equipments as BookingServicesDto[])];
  }

  // handle room layout
  if (roomLayout.length > 0) {
    const layouts = roomLayout.map(layout => ({
      serviceType: ServiceType.Room_Layout,
      status: BookingRequestsStatus.REQUESTED,
      serviceId: layout.id,
      setUpTime: layout.setUpTime ?? 15
    }));

    dtos = [...dtos, ...(layouts as BookingServicesDto[])];
  }

  return dtos;
};
